import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  LogOut,
  Loader2,
  Briefcase,
  DollarSign,
  MapPin,
  TrendingUp,
  Building2,
  RefreshCw,
  Copy,
  Download,
  Check,
  BarChart3,
  PieChart,
  Activity,
  BookOpen,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  X,
  Users,
  Calendar,
  Tag,
  ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Area,
  AreaChart
} from 'recharts';
import html2canvas from 'html2canvas';

// Color palette matching existing design
const CHART_COLORS = {
  primary: '#84cc16', // lime-500
  secondary: '#3b82f6', // blue-500
  tertiary: '#8b5cf6', // purple-500
  quaternary: '#f59e0b', // amber-500
  slate: '#64748b',
};

const SALARY_COLORS = [
  '#84cc16', // lime
  '#22c55e', // green
  '#14b8a6', // teal
  '#06b6d4', // cyan
  '#3b82f6', // blue
  '#8b5cf6', // purple
  '#ec4899', // pink
];

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon: Icon, color = 'lime' }) => {
  const colorClasses = {
    lime: 'bg-lime-50 border-lime-200 text-lime-600',
    blue: 'bg-blue-50 border-blue-200 text-blue-600',
    purple: 'bg-purple-50 border-purple-200 text-purple-600',
    amber: 'bg-amber-50 border-amber-200 text-amber-600',
  };

  return (
    <Card className={`${colorClasses[color]} border`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-600 mb-1">{title}</p>
            <p className="text-3xl font-bold text-slate-900">{value}</p>
            {subtitle && (
              <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`p-3 rounded-full ${colorClasses[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Chart Container with Export Buttons
const ChartContainer = ({ title, subtitle, children, chartId }) => {
  const chartRef = useRef(null);
  const [copying, setCopying] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const captureChart = async () => {
    if (!chartRef.current) return null;
    
    try {
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#ffffff',
        scale: 2, // 2x for crisp images
        logging: false,
        useCORS: true,
      });
      return canvas;
    } catch (error) {
      console.error('Failed to capture chart:', error);
      return null;
    }
  };

  const handleCopy = async () => {
    setCopying(true);
    try {
      const canvas = await captureChart();
      if (canvas) {
        canvas.toBlob(async (blob) => {
          if (blob) {
            await navigator.clipboard.write([
              new ClipboardItem({ 'image/png': blob })
            ]);
            toast.success('Chart copied to clipboard!');
          }
        }, 'image/png');
      }
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy chart');
    } finally {
      setTimeout(() => setCopying(false), 1000);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const canvas = await captureChart();
      if (canvas) {
        const link = document.createElement('a');
        link.download = `varyynt-${chartId}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        toast.success('Chart downloaded!');
      }
    } catch (error) {
      console.error('Failed to download:', error);
      toast.error('Failed to download chart');
    } finally {
      setTimeout(() => setDownloading(false), 1000);
    }
  };

  return (
    <Card className="border-slate-200">
      <div ref={chartRef} className="bg-white rounded-lg">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-heading text-slate-900">{title}</CardTitle>
              {subtitle && (
                <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopy}
                disabled={copying}
                className="h-8 px-2 text-slate-400 hover:text-slate-600"
                title="Copy to clipboard"
              >
                {copying ? <Check className="h-4 w-4 text-lime-500" /> : <Copy className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDownload}
                disabled={downloading}
                className="h-8 px-2 text-slate-400 hover:text-slate-600"
                title="Download as PNG"
              >
                {downloading ? <Check className="h-4 w-4 text-lime-500" /> : <Download className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {children}
        </CardContent>
        <div className="px-6 pb-3">
          <p className="text-[10px] text-slate-400">Varyynt Job Market Analytics</p>
        </div>
      </div>
    </Card>
  );
};

// Custom Tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
        <p className="font-medium text-slate-900">{label}</p>
        <p className="text-lime-600 font-semibold">{payload[0].value.toLocaleString()} jobs</p>
      </div>
    );
  }
  return null;
};

// Custom Tooltip for timeline chart
const TimelineTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
        <p className="font-medium text-slate-900">{label}</p>
        <p className="text-lime-600 font-semibold">{payload[0].value} jobs posted</p>
      </div>
    );
  }
  return null;
};

// Custom Tooltip for companies chart
const CompanyTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
        <p className="font-medium text-slate-900">{label}</p>
        <p className="text-blue-600 font-semibold">{payload[0].value} open positions</p>
      </div>
    );
  }
  return null;
};

// Format salary for display
const formatSalary = (value) => {
  if (!value || value === 0) return 'N/A';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${Math.round(value / 1000)}K`;
  return `$${value}`;
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Segment Stats Strip
const SegmentStats = ({ stats, filterType }) => {
  if (!stats) return null;
  const { top_companies, top_titles, avg_salary } = stats;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 px-6 py-3 bg-slate-50 border-b border-slate-200">
      {/* Top Companies (hide for company filter — user already knows) */}
      {filterType !== 'company' && top_companies?.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
            <Building2 className="h-3 w-3" /> Top Companies
          </p>
          <div className="flex flex-wrap gap-1">
            {top_companies.map((c) => (
              <span key={c.name} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                {c.name} <span className="text-blue-400">·{c.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      {/* Top Titles */}
      {top_titles?.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
            <Briefcase className="h-3 w-3" /> Top Titles
          </p>
          <div className="flex flex-wrap gap-1">
            {top_titles.slice(0, 4).map((t) => (
              <span key={t.title} className="inline-flex items-center gap-1 px-2 py-0.5 bg-lime-100 text-lime-700 rounded-full text-xs font-medium truncate max-w-[160px]" title={t.title}>
                {t.title.length > 22 ? t.title.slice(0, 22) + '…' : t.title}
                <span className="text-lime-500">·{t.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      {/* Avg Salary */}
      {avg_salary != null && (
        <div>
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
            <DollarSign className="h-3 w-3" /> Salary (Avg / Range)
          </p>
          <p className="text-sm font-semibold text-slate-800">{formatSalary(avg_salary)}</p>
          {stats.salary_min != null && stats.salary_max != null && (
            <p className="text-xs text-slate-500">{formatSalary(stats.salary_min)} – {formatSalary(stats.salary_max)}</p>
          )}
        </div>
      )}
    </div>
  );
};

// Chart Drill-Down Modal
const ChartDrillDownModal = ({ filter, onClose, authAxios }) => {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedJob, setSelectedJob] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const LIMIT = 20;

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchJobs = useCallback(async (currentPage, currentSearch) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        filter_type: filter.type,
        filter_value: filter.value,
        page: currentPage,
        limit: LIMIT,
      });
      if (currentSearch) params.append('search', currentSearch);
      const response = await authAxios.get(`/admin/job-analytics/drill-down?${params}`);
      setJobs(response.data.jobs || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
      setStats(response.data.stats || null);
    } catch (err) {
      console.error('Drill-down fetch failed:', err);
      toast.error('Failed to load jobs for this segment');
    } finally {
      setLoading(false);
    }
  }, [authAxios, filter]);

  useEffect(() => {
    fetchJobs(page, search);
  }, [fetchJobs, page, search]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && !selectedJob) onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, selectedJob]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        filter_type: filter.type,
        filter_value: filter.value,
      });
      if (search) params.append('search', search);
      const response = await authAxios.get(
        `/admin/job-analytics/drill-down/export?${params}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const safe = filter.value.replace(/[^a-zA-Z0-9]/g, '-');
      link.download = `varyynt-${filter.type}-${safe}${search ? `-search-${search.replace(/\s+/g, '-')}` : ''}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${total.toLocaleString()} jobs to CSV`);
    } catch (err) {
      console.error('CSV export failed:', err);
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const filterIcons = {
    salary_range: <DollarSign className="h-5 w-5 text-lime-600" />,
    company: <Building2 className="h-5 w-5 text-blue-600" />,
    category: <Tag className="h-5 w-5 text-purple-600" />,
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white rounded-t-xl">
            <div className="flex items-center gap-3">
              {filterIcons[filter.type]}
              <div>
                <h2 className="text-lg font-heading text-slate-900">{filter.label}</h2>
                <p className="text-sm text-slate-500">
                  {loading ? 'Loading…' : `${total.toLocaleString()} job${total !== 1 ? 's' : ''}${search ? ` matching "${search}"` : ''}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                disabled={exporting || loading || total === 0}
                data-testid="drill-down-export-csv"
                className="text-slate-600 hover:text-slate-900 border-slate-300"
              >
                {exporting ? (
                  <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Exporting…</>
                ) : (
                  <><Download className="h-4 w-4 mr-1.5" />Export CSV</>
                )}
              </Button>
              <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="px-6 py-3 border-b border-slate-100 bg-white">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by title or company within this segment…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                data-testid="drill-down-search"
                className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
              />
              {searchInput && (
                <button
                  onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Segment Stats Strip */}
          {!loading && stats && <SegmentStats stats={stats} filterType={filter.type} />}

          {/* Body */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-lime-600" />
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-16">
                <Briefcase className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">
                  {search ? `No jobs matching "${search}" in this segment` : 'No jobs found for this segment'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Job Title</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Company</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Category</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Location</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Salary</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Posted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job) => (
                      <tr
                        key={job.id}
                        className="border-b border-slate-100 hover:bg-lime-50 cursor-pointer transition-colors"
                        onClick={() => setSelectedJob(job)}
                        data-testid={`drill-down-job-row-${job.id}`}
                      >
                        <td className="py-3 px-4">
                          <p className="font-medium text-slate-900 truncate max-w-[220px]">{job.job_title}</p>
                        </td>
                        <td className="py-3 px-4 text-slate-700 text-sm">{job.company_name || 'N/A'}</td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="text-xs">
                            {(job.category_display || 'Uncategorized').split(' ').slice(0, 2).join(' ')}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-slate-600 text-sm truncate max-w-[100px]">{job.location_display}</td>
                        <td className="py-3 px-4 text-slate-700 font-medium text-sm">
                          {job.display_salary ? formatSalary(job.display_salary) : 'N/A'}
                        </td>
                        <td className="py-3 px-4 text-slate-500 text-sm">{formatDate(job.date_posted)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination Footer */}
          {!loading && total > 0 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <p className="text-sm text-slate-500">
                {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, total)} of {total.toLocaleString()}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm text-slate-600 px-2">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedJob && (
        <JobDetailModal
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          authAxios={authAxios}
        />
      )}
    </>
  );
};

// Job Detail Modal Component
const JobDetailModal = ({ job, onClose, onFindMatchingUsers, authAxios }) => {
  const [matchingUsers, setMatchingUsers] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const findMatchingUsers = async () => {
    setLoadingUsers(true);
    try {
      // Fetch candidates who have actual V-FIT matches for this job
      const response = await authAxios.get(`/admin/job-analytics/job-matches/${job.id}`);
      setMatchingUsers(response.data);
    } catch (error) {
      console.error('Failed to find matching users:', error);
      toast.error('Failed to find matching users');
    } finally {
      setLoadingUsers(false);
    }
  };

  if (!job) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex-1 pr-4">
            <h2 className="text-xl font-heading text-slate-900 mb-1">{job.job_title}</h2>
            <div className="flex items-center gap-3 text-slate-600">
              <span className="flex items-center gap-1">
                <Building2 className="h-4 w-4" />
                {job.company_name || 'Unknown Company'}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {job.location_display || 'Not specified'}
              </span>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </Button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Quick Info Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-lime-50 border border-lime-200 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">Salary Range</p>
              <p className="font-semibold text-slate-900">
                {job.salary_min && job.salary_max 
                  ? `${formatSalary(job.salary_min)} - ${formatSalary(job.salary_max)}`
                  : job.display_salary ? formatSalary(job.display_salary) : 'Not disclosed'}
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">Category</p>
              <p className="font-semibold text-slate-900 truncate">{job.category_display || 'Uncategorized'}</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">Work Type</p>
              <p className="font-semibold text-slate-900">{job.job_location_raw || 'Not specified'}</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">Posted</p>
              <p className="font-semibold text-slate-900">{formatDate(job.date_posted)}</p>
            </div>
          </div>

          {/* Job Description */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Job Description</h3>
            <div 
              className="prose prose-sm max-w-none text-slate-600 bg-slate-50 rounded-lg p-4 max-h-60 overflow-y-auto"
              dangerouslySetInnerHTML={{ __html: job.description_raw || '<p>No description available</p>' }}
            />
          </div>

          {/* Find Matching Users Section */}
          <div className="border-t border-slate-200 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Find Matching Candidates
              </h3>
              <Button 
                onClick={findMatchingUsers} 
                disabled={loadingUsers}
                size="sm"
                className="bg-lime-600 hover:bg-lime-700"
              >
                {loadingUsers ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Find Candidates
                  </>
                )}
              </Button>
            </div>
            
            {matchingUsers && (
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-600 mb-3">
                  Found <span className="font-semibold text-lime-600">{matchingUsers.total}</span> candidates with V-FIT matches
                </p>
                {matchingUsers.candidates && matchingUsers.candidates.length > 0 ? (
                  <div className="space-y-2">
                    {matchingUsers.candidates.slice(0, 5).map((candidate) => (
                      <div
                        key={candidate.user_id}
                        className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200"
                      >
                        <div>
                          <p className="font-medium text-slate-900">{candidate.title || 'Unknown Title'}</p>
                          <p className="text-sm text-slate-500">User ID: {candidate.user_id.slice(0, 8)}...</p>
                        </div>
                        <Badge variant="outline" className={
                          candidate.match_strength === 'strong' 
                            ? 'bg-lime-100 border-lime-500 text-lime-700' 
                            : candidate.match_strength === 'clear'
                            ? 'bg-blue-100 border-blue-500 text-blue-700'
                            : 'bg-amber-100 border-amber-500 text-amber-700'
                        }>
                          V-FIT: {candidate.vfit_score}
                        </Badge>
                      </div>
                    ))}
                    {matchingUsers.total > 5 && (
                      <p className="text-center text-sm text-slate-500 pt-2">
                        + {matchingUsers.total - 5} more candidates
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 italic">No candidates have V-FIT matches for this job yet.</p>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-slate-200 bg-slate-50">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {job.apply_url && (
            <a href={job.apply_url} target="_blank" rel="noopener noreferrer">
              <Button className="bg-lime-600 hover:bg-lime-700">
                <ExternalLink className="h-4 w-4 mr-2" />
                View Original Posting
              </Button>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// Data Explorer Component
const DataExplorer = ({ authAxios }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [locationType, setLocationType] = useState('');
  const [category, setCategory] = useState('');
  const [sortBy, setSortBy] = useState('date_posted');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedJob, setSelectedJob] = useState(null);
  
  // Candidate match score feature
  const [matchableCandidates, setMatchableCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState('');
  const [candidateMatches, setCandidateMatches] = useState({});
  const [loadingMatches, setLoadingMatches] = useState(false);

  // Load matchable candidates on mount
  useEffect(() => {
    const loadCandidates = async () => {
      try {
        const response = await authAxios.get('/admin/job-analytics/matchable-candidates');
        setMatchableCandidates(response.data.candidates || []);
      } catch (error) {
        console.error('Failed to load matchable candidates:', error);
      }
    };
    loadCandidates();
  }, [authAxios]);

  // Load candidate matches when a candidate is selected
  useEffect(() => {
    if (!selectedCandidate) {
      setCandidateMatches({});
      return;
    }
    
    const loadMatches = async () => {
      setLoadingMatches(true);
      try {
        const response = await authAxios.get(`/admin/job-analytics/candidate-matches/${selectedCandidate}`);
        setCandidateMatches(response.data.matches || {});
      } catch (error) {
        console.error('Failed to load candidate matches:', error);
        toast.error('Failed to load match scores');
      } finally {
        setLoadingMatches(false);
      }
    };
    loadMatches();
  }, [authAxios, selectedCandidate]);

  // State for showing only matched jobs
  const [showOnlyMatched, setShowOnlyMatched] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);

  const handleExportAll = async () => {
    setExportingAll(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (locationType) params.append('location_type', locationType);
      if (category) params.append('category', category);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      const response = await authAxios.get(
        `/admin/job-analytics/export?${params}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const parts = [];
      if (category) parts.push(category.replace(/\s+/g, '-'));
      if (locationType) parts.push(locationType);
      if (search) parts.push(`search-${search.replace(/\s+/g, '-')}`);
      link.download = `varyynt-jobs-${parts.length ? parts.join('-') : 'all'}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${total.toLocaleString()} jobs to CSV`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export jobs');
    } finally {
      setExportingAll(false);
    }
  };

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      if (locationType) params.append('location_type', locationType);
      if (category) params.append('category', category);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      
      // If showing only matched jobs, pass the job IDs to filter
      if (showOnlyMatched && selectedCandidate && Object.keys(candidateMatches).length > 0) {
        params.append('job_ids', Object.keys(candidateMatches).join(','));
      }

      const response = await authAxios.get(`/admin/job-analytics/jobs?${params.toString()}`);
      setJobs(response.data.jobs || []);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load jobs:', error);
      toast.error('Failed to load jobs data');
    } finally {
      setLoading(false);
    }
  }, [authAxios, page, search, locationType, category, sortBy, sortOrder, showOnlyMatched, selectedCandidate, candidateMatches]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Reset showOnlyMatched when candidate is cleared
  useEffect(() => {
    if (!selectedCandidate) {
      setShowOnlyMatched(false);
    }
  }, [selectedCandidate]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setSearchInput('');
    setLocationType('');
    setCategory('');
    setSelectedCandidate('');
    setPage(1);
  };

  // Helper to get match score badge
  const getMatchBadge = (jobId) => {
    const match = candidateMatches[jobId];
    if (!match) return null;
    
    const strengthColors = {
      strong: 'bg-lime-100 text-lime-700 border-lime-300',
      clear: 'bg-blue-100 text-blue-700 border-blue-300',
      emerging: 'bg-amber-100 text-amber-700 border-amber-300',
      weak: 'bg-slate-100 text-slate-600 border-slate-300'
    };
    
    return (
      <Badge variant="outline" className={`text-xs font-semibold ${strengthColors[match.strength] || strengthColors.emerging}`}>
        {match.score}
      </Badge>
    );
  };

  return (
    <div className="mt-8">
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-heading text-slate-900 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-lime-600" />
                Data Explorer
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                Browse and search all {total.toLocaleString()} job listings
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportAll}
              disabled={exportingAll || loading || total === 0}
              data-testid="data-explorer-export-csv"
              className="text-slate-600 hover:text-slate-900 border-slate-300"
            >
              {exportingAll ? (
                <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Exporting…</>
              ) : (
                <><Download className="h-4 w-4 mr-1.5" />Export CSV{(search || locationType || category) ? ' (filtered)' : ''}</>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Candidate Match Score Selector */}
          {matchableCandidates.length > 0 && (
            <div className="mb-4 p-4 bg-gradient-to-r from-lime-50 to-emerald-50 border border-lime-200 rounded-lg">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-lime-600" />
                  <span className="text-sm font-medium text-slate-700">Compare with Candidate:</span>
                </div>
                <Select value={selectedCandidate || 'none'} onValueChange={(val) => { setSelectedCandidate(val === 'none' ? '' : val); setPage(1); }}>
                  <SelectTrigger className="w-[320px] bg-white">
                    <SelectValue placeholder="Select a candidate to see V-FIT scores..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (no comparison)</SelectItem>
                    {matchableCandidates.map((c) => (
                      <SelectItem key={c.user_id} value={c.user_id}>
                        {c.title} ({c.match_count} matches, best: {c.best_score})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {loadingMatches && <Loader2 className="h-4 w-4 animate-spin text-lime-600" />}
                {selectedCandidate && !loadingMatches && Object.keys(candidateMatches).length > 0 && (
                  <Button
                    variant={showOnlyMatched ? "default" : "outline"}
                    size="sm"
                    onClick={() => { setShowOnlyMatched(!showOnlyMatched); setPage(1); }}
                    className={showOnlyMatched ? "bg-lime-600 hover:bg-lime-700" : "border-lime-300 text-lime-700 hover:bg-lime-50"}
                  >
                    {showOnlyMatched ? (
                      <>
                        <Check className="h-4 w-4 mr-1" />
                        Showing {Object.keys(candidateMatches).length} matched jobs
                      </>
                    ) : (
                      <>
                        <Filter className="h-4 w-4 mr-1" />
                        Show only matched jobs ({Object.keys(candidateMatches).length})
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by job title or company..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button type="submit" variant="outline">
                Search
              </Button>
            </form>
            
            <div className="flex gap-2">
              <Select value={locationType} onValueChange={(val) => { setLocationType(val === 'all' ? '' : val); setPage(1); }}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Work Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="remote">Remote</SelectItem>
                  <SelectItem value="onsite">On-site</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={category} onValueChange={(val) => { setCategory(val === 'all' ? '' : val); setPage(1); }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Clinical Research">Clinical Research</SelectItem>
                  <SelectItem value="Corporate">Corporate Functions</SelectItem>
                  <SelectItem value="Sales">Sales</SelectItem>
                  <SelectItem value="Marketing">Marketing</SelectItem>
                  <SelectItem value="Operations">Operations</SelectItem>
                  <SelectItem value="IT">IT</SelectItem>
                  <SelectItem value="Medical Affairs">Medical Affairs</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={sortBy} onValueChange={(val) => { setSortBy(val); setPage(1); }}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Sort By" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date_posted">Date Posted</SelectItem>
                  <SelectItem value="salary">Salary</SelectItem>
                  <SelectItem value="company">Company</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                title={sortOrder === 'desc' ? 'Descending' : 'Ascending'}
              >
                <ArrowUpDown className={`h-4 w-4 ${sortOrder === 'asc' ? 'rotate-180' : ''}`} />
              </Button>
              
              {(search || locationType || category || selectedCandidate) && (
                <Button variant="ghost" onClick={clearFilters} className="text-slate-500">
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Jobs Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-lime-600" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No jobs found matching your criteria</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Job Title</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Company</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Category</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Location</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Salary</th>
                      {selectedCandidate && (
                        <th className="text-left py-3 px-4 text-xs font-semibold text-lime-600 uppercase tracking-wider">V-FIT</th>
                      )}
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Posted</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job) => (
                      <tr 
                        key={job.id} 
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => setSelectedJob(job)}
                      >
                        <td className="py-3 px-4">
                          <p className="font-medium text-slate-900 truncate max-w-xs">{job.job_title}</p>
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-slate-700">{job.company_name || 'N/A'}</p>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="text-xs">
                            {job.category_display?.split(' ').slice(0, 2).join(' ') || 'Uncategorized'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-slate-600 text-sm truncate max-w-[120px]">{job.location_display}</p>
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-slate-700 font-medium">
                            {job.display_salary ? formatSalary(job.display_salary) : 'N/A'}
                          </p>
                        </td>
                        {selectedCandidate && (
                          <td className="py-3 px-4">
                            {getMatchBadge(job.id) || <span className="text-slate-400 text-sm">-</span>}
                          </td>
                        )}
                        <td className="py-3 px-4">
                          <p className="text-slate-500 text-sm">{formatDate(job.date_posted)}</p>
                        </td>
                        <td className="py-3 px-4">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={(e) => { e.stopPropagation(); setSelectedJob(job); }}
                            className="text-lime-600 hover:text-lime-700"
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  Showing {((page - 1) * 20) + 1} - {Math.min(page * 20, total)} of {total.toLocaleString()} jobs
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-slate-600 px-3">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Job Detail Modal */}
      {selectedJob && (
        <JobDetailModal 
          job={selectedJob} 
          onClose={() => setSelectedJob(null)}
          authAxios={authAxios}
        />
      )}
    </div>
  );
};

export default function AdminJobAnalyticsPage() {
  const { authAxios, logout } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [charts, setCharts] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' or 'explorer'
  const [drillDownFilter, setDrillDownFilter] = useState(null); // { type, value, label }

  const openDrillDown = (type, value, label) => setDrillDownFilter({ type, value, label });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, chartsRes] = await Promise.all([
        authAxios.get('/admin/job-analytics/summary'),
        authAxios.get('/admin/job-analytics/charts')
      ]);
      
      setSummary(summaryRes.data);
      setCharts(chartsRes.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to load job analytics:', error);
      toast.error('Failed to load job analytics data');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading && !summary) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-lime-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading job market analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <Link to="/" className="text-xl font-heading text-slate-900">
                varyynt
              </Link>
              <nav className="hidden md:flex items-center gap-1">
                <Link to="/admin" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Overview
                </Link>
                <Link to="/admin/candidates" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Candidates
                </Link>
                <Link to="/admin/job-analytics" className="px-4 py-2 text-sm font-medium text-lime-700 bg-lime-50 rounded-lg">
                  Job Market
                </Link>
                <Link to="/admin/analytics" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Analytics
                </Link>
                <Link to="/admin/market-pulse" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Market Pulse
                </Link>
                <Link to="/admin/messages" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Messages
                </Link>
                <Link to="/admin/support" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  User Support
                </Link>
                <Link to="/admin/job-import" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Job Import
                </Link>
              </nav>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/admin/guide">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Admin Guide
                </Button>
              </Link>
              <Link to="/brand" target="_blank">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600">
                  Brand Guide
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={logout} className="text-slate-400 hover:text-slate-600">
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-heading text-slate-900">Job Market Analytics</h1>
            <p className="text-slate-500">Insights from {summary?.total_jobs?.toLocaleString() || 0} active job listings</p>
          </div>
          <div className="flex items-center gap-3">
            {lastRefresh && (
              <span className="text-xs text-slate-400">
                Last updated: {lastRefresh.toLocaleTimeString()}
              </span>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={loadData}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <KPICard
            title="Total Active Jobs"
            value={summary?.total_jobs?.toLocaleString() || '0'}
            icon={Briefcase}
            color="lime"
          />
          <KPICard
            title="Average Salary"
            value={formatSalary(summary?.average_salary)}
            subtitle={`${summary?.salary_data_coverage || 0}% have salary data`}
            icon={DollarSign}
            color="blue"
          />
          <KPICard
            title="Remote Roles"
            value={`${summary?.remote_percentage || 0}%`}
            subtitle="of all positions"
            icon={MapPin}
            color="purple"
          />
          <KPICard
            title="New This Week"
            value={summary?.new_jobs_7_days?.toLocaleString() || '0'}
            subtitle="posted in last 7 days"
            icon={TrendingUp}
            color="amber"
          />
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Salary Distribution */}
          <ChartContainer 
            title="Salary Distribution" 
            subtitle="Click a bar to explore jobs in that range"
            chartId="salary-distribution"
          >
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts?.salary_distribution || []} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="range" 
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis 
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="count"
                    radius={[4, 4, 0, 0]}
                    cursor="pointer"
                    onClick={(data) => openDrillDown('salary_range', data.range, `Salary Range: ${data.range}`)}
                  >
                    {(charts?.salary_distribution || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={SALARY_COLORS[index % SALARY_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
              <Filter className="h-3 w-3" /> Click any bar to see matching jobs
            </p>
          </ChartContainer>

          {/* Category Breakdown */}
          <ChartContainer 
            title="Top Job Categories" 
            subtitle="Click a bar to explore jobs in that category"
            chartId="category-breakdown"
          >
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={charts?.category_breakdown || []} 
                  layout="vertical"
                  margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis 
                    dataKey="category" 
                    type="category" 
                    tick={{ fontSize: 10, fill: '#64748b' }}
                    width={100}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="count"
                    fill={CHART_COLORS.primary}
                    radius={[0, 4, 4, 0]}
                    cursor="pointer"
                    onClick={(data) => openDrillDown('category', data.category, `Category: ${data.category}`)}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
              <Filter className="h-3 w-3" /> Click any bar to see matching jobs
            </p>
          </ChartContainer>

          {/* Hiring Timeline */}
          <ChartContainer 
            title="Posting Activity (30 Days)" 
            subtitle="Daily job posting trend"
            chartId="hiring-timeline"
          >
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={charts?.hiring_timeline || []} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                  <defs>
                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10, fill: '#64748b' }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getMonth() + 1}/${date.getDate()}`;
                    }}
                    interval={4}
                  />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                  <Tooltip content={<TimelineTooltip />} />
                  <Area 
                    type="monotone" 
                    dataKey="count" 
                    stroke={CHART_COLORS.primary} 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorCount)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </ChartContainer>

          {/* Top Companies */}
          <ChartContainer 
            title="Top Hiring Companies" 
            subtitle="Click a bar to see all jobs from that company"
            chartId="top-companies"
          >
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={charts?.top_companies || []} 
                  layout="vertical"
                  margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} />
                  <YAxis 
                    dataKey="company" 
                    type="category" 
                    tick={{ fontSize: 10, fill: '#64748b' }}
                    width={100}
                  />
                  <Tooltip content={<CompanyTooltip />} />
                  <Bar
                    dataKey="count"
                    fill={CHART_COLORS.secondary}
                    radius={[0, 4, 4, 0]}
                    cursor="pointer"
                    onClick={(data) => openDrillDown('company', data.company, `${data.company} — All Jobs`)}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
              <Filter className="h-3 w-3" /> Click any bar to see all jobs from that company
            </p>
          </ChartContainer>
        </div>

        {/* Data Explorer */}
        <DataExplorer authAxios={authAxios} />
      </main>

      {/* Chart Drill-Down Modal */}
      {drillDownFilter && (
        <ChartDrillDownModal
          filter={drillDownFilter}
          onClose={() => setDrillDownFilter(null)}
          authAxios={authAxios}
        />
      )}
    </div>
  );
}
